import * as React from 'react';
import type {ReferralFiltersPayload} from 'src/types/referral-v2';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch} from 'react-redux';
import * as reduxApi from 'src/utils/redux-api-v2';
import {
  type ToastProps,
  type ToastSemanticType,
  CircularLoader,
  Button,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  SubTitleLarge,
  BodyMedium,
  Toast,
  ToastBody,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/index';
import css from 'src/components/referral-v2/payouts/payouts.css';


const ReferralConfirmModal = ({
  selectedReferralCount,
  actionableCandidateCount,
  handleBulkUpdate,
  status,
  totalRecords,
  isAllItemsSelected,
  from,
  filters,
  isModalOpen,
  onClose,
}: {
  selectedReferralCount: number;
  actionableCandidateCount: number;
  handleBulkUpdate: (arg1: string) => Promise<unknown>;
  totalRecords: number;
  isAllItemsSelected: boolean;
  status: string;
  from: string;
  filters: ReferralFiltersPayload[];
  isModalOpen: boolean;
  onClose: (arg1: React.SyntheticEvent<HTMLElement>) => unknown;
}) => {
  const dispatch = useDispatch();
  const {t, makeKey} = useLabelTranslation();
  const [isSubmit, setIsSubmit] = React.useState(false);
  const [bulkActionCount, setBulkActionCount] = React.useState<any>(null);
  const [bulkActionCountLoading, setBulkActionCountLoading] =
    React.useState(false);

  const handleSubmit = async () => {
    setIsSubmit(true);
    await handleBulkUpdate(status);
    setIsSubmit(false);
    setBulkActionCount(null);
  };

  const referralCount = isAllItemsSelected
    ? totalRecords
    : selectedReferralCount;

  const actionReferralCount = isAllItemsSelected
    ? bulkActionCount
    : actionableCandidateCount;

  React.useEffect(() => {
    if (isAllItemsSelected && isModalOpen) {
      let payload = {filters};
      if (from === 'tracking') {
        // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'status' does not exist in type '{ filters: ReferralFiltersPayload[]; }'.
        payload = {...payload, status};
      }
      setBulkActionCountLoading(true);
      dispatch(
        reduxApi.post(`referral/${from}/bulk-update/count`, payload),
      ).then(({count}: {count: number}) => {
        setBulkActionCount(count);
        setBulkActionCountLoading(false);
      });
    }
  }, [status, isModalOpen]);

  return (
    <Modal
      onClose={onClose}
      isOpen={isModalOpen}
      initialFocus={2}
      size="medium"
    >
      <ModalHeader onCloseButtonClick={onClose}>
        <SubTitleLarge>{`${t(
          'Change_the_Status',
          'Change the Status',
        )}?`}</SubTitleLarge>
      </ModalHeader>
      <ModalBody className={css.confirmPaymentModalText}>
        {bulkActionCountLoading ? (
          <CircularLoader className={css.loader} size="small" />
        ) : (
          <>
            <BodyMedium>
              {`${t('Only', 'Only')} `}
              {actionReferralCount}
              {` ${t('of_your', 'of your')} `}
              {referralCount}
              {` ${t(
                'selected_referrals_can_be_mark_as',
                'selected referrals can be mark as',
              )} `}
              {t(makeKey(status), status)}
            </BodyMedium>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button type="tertiary" onClick={onClose}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          isLoading={isSubmit}
          disabled={!actionReferralCount}
          onClick={handleSubmit}
        >
          {t('Confirm', 'Confirm')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ReferralConfirmModal;

export const toastMessage = ({
  message,
  title,
  semantic = 'success',
}: {
  message: string;
  title?: string;
  semantic?: ToastSemanticType;
}): React.ReactElement<
  React.ComponentProps<(props: ToastProps) => React.ReactNode>
> => {
  return (
    <Toast initialFocus={0} semantic={semantic}>
      {title && <ToastTitle>{title}</ToastTitle>}
      <ToastBody>{message}</ToastBody>
    </Toast>
  );
};
